import React, { useEffect, useState, useRef, useMemo } from 'react';
import axios from '../../helper/axios';
import { useParams, useLocation, useSearchParams, useNavigationType } from 'react-router-dom';
import Footer from '../../component/Footer';
import Header from '../../component/Header';
import { addToWishlist } from '../../action';
import Productfilter from '../../component/Productfilter';
import Productmobilefilter from '../../component/Productmobilefilter';
import { useDispatch, useSelector } from 'react-redux';
import InfiniteProducts from '../../component/Common/InfiniteProducts';
import StoreHeaderBanner from './StoreHeaderBanner';
import ComingSoon from "../../component/ComingSoon";
import { useGetBannerProductsInfiniteQuery } from '../../store/api/productsApi';
import useActivePromotionsMap, { getPromotionLabelForLocation } from '../../hooks/useActivePromotionsMap';
import { filterGenderOptions, parseBrendiParam, sizesMatch } from '../../helper/filterOptions';

export default function Products() {
  const { name } = useParams();
  const brandCaps = name ? name.toUpperCase() : '';
  // PLP scroll restore: vetëm për këtë brand (jo global)
  const plpStorageScope = name ? `banner_${brandCaps}` : null;
  const plpScrollKey = plpStorageScope ? `plp_scrollY_${plpStorageScope}` : 'plp_scrollY';
  const plpProductKey = plpStorageScope ? `plp_productId_${plpStorageScope}` : 'plp_productId';
  const filterStorageKey = `bannerFilters_${brandCaps}`;
  
  const location = useLocation();
  const navigationType = useNavigationType();
  const [searchParams, setSearchParams] = useSearchParams();
  const gender = searchParams.get('gender');
  const brendiQuery = searchParams.get('brendi') || '';
  const urlPage = Math.max(1, Number(searchParams.get('page') || 1) || 1);

  const dispatch = useDispatch();
  const [storeInfo, setStoreInfo] = useState();
  const wishlistItems = useSelector((state) => state?.cart?.wishlistItems);
  const [storeCodeData, setStoreCodeData] = useState(null);
  const [availableSubBrands, setAvailableSubBrands] = useState(null);
  const [brandFacets, setBrandFacets] = useState(null);
  const [facetStatus, setFacetStatus] = useState('loading');
  const { promotionsByLocation, loading: promotionsLoading } = useActivePromotionsMap();

  // Blloko browser-in nga scroll automatik kur kthehesh Back
  useEffect(() => {
    if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    return () => {
      if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
    };
  }, []);

  // Restore filters from location.state or sessionStorage if available (when navigating back from product details)
  const savedFilters = location.state?.filters || (() => {
    try {
      const saved = sessionStorage.getItem(filterStorageKey);
      if (saved) {
        const parsedFilters = JSON.parse(saved);
  
        const maxAge = 30 * 60 * 1000;
        if (parsedFilters.timestamp && Date.now() - parsedFilters.timestamp > maxAge) {
          sessionStorage.removeItem(filterStorageKey);
          return null;
        }
  
        return parsedFilters;
      }
      return null;
    } catch {
      return null;
    }
  })();
  const hasRestoredFilters = useRef(false);
  const lastLocationKey = useRef(location.key);

  // Filter states - initialize from location.state if available, otherwise empty
  const [categoryCheck, setCategoryCheck] = useState(() => {
    return savedFilters?.categoryCheck || [];
  });
  const [brandCheck, setBrandCheck] = useState(() => {
    return savedFilters?.brandCheck || [];
  });
  const [subBrandCheck, setSubBrandCheck] = useState(() => parseBrendiParam(searchParams));
  const [sizeCheck, setSizeCheck] = useState(() => {
    return savedFilters?.sizeCheck || [];
  });
  const [gtPrice, setGtPrice] = useState(() => {
    return savedFilters?.gtPrice;
  });
  const [ltPrice, setLtPrice] = useState(() => {
    return savedFilters?.ltPrice;
  });
  const [sortPrice, setSortPrice] = useState(() => {
    return savedFilters?.sortPrice || '';
  });
  const [offers, setOffers] = useState(() => {
    return savedFilters?.offers !== undefined ? savedFilters.offers : "";
  });
  const [selectedGender, setSelectedGender] = useState(() => {
    return savedFilters?.selectedGender !== undefined ? savedFilters.selectedGender : undefined;
  });

  const per_page = 40;
  const listContainerRef = useRef(null);
  const didNormalizePageRef = useRef(false);
  const lastBrandRef = useRef(name);

  // Kur kalojmë nga një brand te një tjetër, mos e trashëgo page të vjetër (p.sh. ?page=2 nga /brands/hugo).
  useEffect(() => {
    const prev = lastBrandRef.current;
    lastBrandRef.current = name;
    if (!prev || !name) return;
    if (prev === name) return;
    if (navigationType === "POP") return;

    const next = new URLSearchParams(searchParams);
    next.set("page", "1");
    setSearchParams(next, { replace: true });
    didNormalizePageRef.current = true;
  }, [name, navigationType, searchParams, setSearchParams]);

  // Nëse hapet direkt një URL me ?page>1 (p.sh. rifreskim),
  // dhe nuk kemi state për t'u kthyer mbrapa nga produkti,
  // reseto page në 1 që lista të sillet si hapje e re.
  useEffect(() => {
    if (didNormalizePageRef.current) return;
    if (navigationType !== 'POP') return;
    if (urlPage <= 1) return;

    let hasScrollState = false;
    let hasProductState = false;
    try {
      hasScrollState = !!sessionStorage.getItem(plpScrollKey);
      hasProductState = !!sessionStorage.getItem(plpProductKey);
    } catch {}

    // Nëse s'ka asnjë state për kthim mbrapa, trajtoje si rifreskim/hapje direkte
    if (!hasScrollState && !hasProductState) {
      didNormalizePageRef.current = true;
      const next = new URLSearchParams(searchParams);
      next.set('page', '1');
      setSearchParams(next, { replace: true });
    }
  }, [navigationType, urlPage, searchParams, setSearchParams, plpScrollKey, plpProductKey]);

  // Build query params for RTK Query
  const productsQueryParams = useMemo(() => {
    const mergedCatId = [...new Set(categoryCheck.flat())];

    // If sub-brands are selected, ONLY show those sub-brands (format: "MAINBRAND-SUBBRAND")
    // Otherwise, do not send a brand filter so we show everything for this store
    let allBrands = [];
    if (subBrandCheck.length > 0) {
      allBrands = subBrandCheck.map(subBrand => `${brandCaps}-${subBrand}`);
    }



    return {
      gender,
      storeCode: storeCodeData,
      brands: allBrands,
      sizes: sizeCheck,
      gtPrice,
      ltPrice,
      sort: sortPrice,
      id: '',
      catId: mergedCatId,
      per_page,
      discount: offers,
      selectedGender
    };
  }, [gender, storeCodeData, brandCaps, subBrandCheck, sizeCheck, gtPrice, ltPrice, sortPrice, categoryCheck, offers, selectedGender]);

  // RTK Query for products 
  const {
    data: productsResponse,
    isLoading,
    isFetching,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage
  } = useGetBannerProductsInfiniteQuery(productsQueryParams, {
    skip: !storeCodeData && !brandCaps, // Fetch when storeCode OR brand is available
    refetchOnMountOrArgChange: false,
    refetchOnReconnect: false,
    refetchOnFocus: false
  });

  const loadedPagesCount = productsResponse?.pages?.length || 0;
  const productCount = productsResponse?.pages?.[0]?.totalCount;

  const products = useMemo(() => {
    const pages = productsResponse?.pages || [];
    const map = new Map();

    pages.forEach(page => {
      const list = Array.isArray(page?.products) ? page.products : (Array.isArray(page) ? page : []);
      list.forEach(p => {
        // skip non-product objects like { msg: "No products found" } //
        if (!p || typeof p !== 'object' || p.msg) return;

        const key = p.externalId ?? p._id ?? p.id ?? JSON.stringify(p);
        if (!map.has(key)) map.set(key, p);
      });
    });

    return Array.from(map.values());
  }, [productsResponse]);

  const addItemToWishlist = (res) => {
    let updatedList = [...(wishlistItems || [])];
    const index = updatedList.findIndex((x) => x._id === res._id);
    if (index === -1) {
      updatedList.push(res);
    } else {
      updatedList.splice(index, 1);
    }
    dispatch(addToWishlist(updatedList));
  };

  //const setCategoryPush = (e) => setCategoryCheck(prev => [...prev, e]);

  const setCategoryToggleByLength = (e) => {
    setCategoryCheck(prev => {
      const safePrev = Array.isArray(prev) ? prev : [];
      // Convert single ID to array if needed
      const categoryArray = Array.isArray(e) ? e : [e];
      const categoryLength = categoryArray.length;
      
      // Check if an array with the same length exists
      const existsSameLength = safePrev.some(arr => 
        Array.isArray(arr) && 
        arr.length === categoryLength &&
        arr.every((id, idx) => String(id) === String(categoryArray[idx]))
      );
      
      if (existsSameLength) {
        // Remove the matching array
        const next = safePrev.filter(arr => 
          !(Array.isArray(arr) && 
            arr.length === categoryLength &&
            arr.every((id, idx) => String(id) === String(categoryArray[idx])))
        );
        return next;
      } else {
        // Add the new array
        const next = [...safePrev, categoryArray];
        return next;
      }
    });
  };

  const setBrandPush = (e) => setBrandCheck(prev => prev.includes(e) ? prev.filter(x => x !== e) : [...prev, e]);

  const syncBrendiParam = (selection) => {
    const next = new URLSearchParams(searchParams);
    if (!selection.length) next.delete('brendi');
    else next.set('brendi', selection.join(','));
    if ((searchParams.get('brendi') || '') === (next.get('brendi') || '')) return;
    setSearchParams(next, { replace: true });
  };

  const setSubBrandPush = (e) => {
    const value = String(e || '').trim().toUpperCase();
    if (!value) return;
    const newSelection = subBrandCheck.includes(value)
      ? subBrandCheck.filter((item) => item !== value)
      : [...subBrandCheck, value];
    setSubBrandCheck(newSelection);
    syncBrendiParam(newSelection);
  };

  const setSizePush = (e) => setSizeCheck(prev => prev.some((item) => sizesMatch(item, e)) ? prev.filter((item) => !sizesMatch(item, e)) : [...prev, e]);
  const setPricePush = (min, max) => {
    setGtPrice(min);
    setLtPrice(max);
  };

  const setSortPush = (e) => setSortPrice(e);
  const setOfferPushData = (e) => setOffers(e);
  const setGenderPushData = (e) => setSelectedGender(e);

  const resetFilter = () => {
    setSortPrice();
    setOffers();
    setSelectedGender();
    setLtPrice();
    setGtPrice();
    setBrandCheck([]);
    setSubBrandCheck([]);
    syncBrendiParam([]);
    setSizeCheck([]);
    setCategoryCheck([]);
    
    // Clear sessionStorage to prevent filters from being restored (brand-specific)
    try {
      sessionStorage.removeItem(filterStorageKey);
    } catch (e) {
      console.warn('Failed to clear filters from sessionStorage:', e);
    }
  };

  const getStoreInfo = async () => {
    try {
      const data = { storeCode: brandCaps };
      const res = await axios.post('/storedetails', data);
      setStoreInfo(res?.data?.data);
      // Multiple stores can share one brand slug (e.g. Mango → 053 + 054)
      const codes = res?.data?.storeCodes;
      setStoreCodeData(
        Array.isArray(codes) && codes.length > 0
          ? codes
          : res?.data?.data?.storeCode
      );
    } catch (error) {
      console.error('Fetch error:', error);
    }
  };

  const getAvailableSubBrands = async () => {
    try {

      // Use the new API endpoint to get sub-brands directly
      const data = {
        mainBrand: name.toUpperCase()
      };
      const res = await axios.post('/get-subbrands', data);




      if (res.data && Array.isArray(res.data)) {

        setAvailableSubBrands(res.data.length > 0 ? res.data : []);
      } else {

        setAvailableSubBrands([]);
      }
    } catch (error) {

      setAvailableSubBrands([]);
    }
  };


  useEffect(() => {
    getStoreInfo();
    getAvailableSubBrands();
  }, [name]);

  // Nënbrendi vjen nga ?brendi= në URL (p.sh. /brands/stilium?brendi=UGG).
  useEffect(() => {
    const fromUrl = parseBrendiParam(searchParams);
    setSubBrandCheck((prev) => {
      if (prev.length === fromUrl.length && prev.every((item, index) => item === fromUrl[index])) {
        return prev;
      }
      return fromUrl;
    });
  }, [name, brendiQuery, searchParams]);

  useEffect(() => {
    if (!brandCaps) return undefined;
    let cancelled = false;
    setFacetStatus((current) => (current === 'ready' ? 'ready' : 'loading'));

    const loadFacets = async () => {
      const subBrands = subBrandCheck.map((subBrand) => `${brandCaps}-${subBrand}`);
      const res = await axios.post('/brand-facets', {
        mainBrand: brandCaps,
        storeCode: storeCodeData,
        brands: subBrands,
        gender,
        selectedGender,
        sizes: sizeCheck,
        catId: [...new Set(categoryCheck.flat())],
        discount: offers,
        gtPrice,
        ltPrice,
      });
      if (cancelled) return;
      setBrandFacets(res.data || { sizes: [], categories: [], genders: [] });
      setFacetStatus('ready');
    };

    loadFacets().catch(() => {
      if (cancelled) return;
      setBrandFacets(null);
      setFacetStatus('error');
    });

    return () => {
      cancelled = true;
    };
  }, [brandCaps, storeCodeData, subBrandCheck, gender, selectedGender, sizeCheck, categoryCheck, offers, gtPrice, ltPrice]);

  useEffect(() => {
    if (facetStatus !== 'ready' || !brandFacets) return;
    const allowedSizes = brandFacets.sizes || [];
    const allowedCategories = new Set((brandFacets.categories || []).map(String));
    const allowedGenders = filterGenderOptions(brandFacets.genders || []);

    setSizeCheck((prev) => {
      const next = prev.filter((size) => allowedSizes.some((item) => sizesMatch(item, size)));
      return next.length === prev.length ? prev : next;
    });
    setCategoryCheck((prev) => {
      const next = prev.filter((entry) => {
        const ids = Array.isArray(entry) ? entry : [entry];
        return ids.some((id) => allowedCategories.has(String(id)));
      });
      return next.length === prev.length ? prev : next;
    });
    setSelectedGender((prev) => {
      if (!prev) return prev;
      const stillThere = allowedGenders.some((option) => option.value.toLowerCase() === String(prev).toLowerCase());
      return stillThere ? prev : undefined;
    });
  }, [brandFacets, facetStatus]);

  // Restore filters when location.state changes (e.g., when navigating back)
  useEffect(() => {
    const currentFilters = location.state?.filters;
    const locationKeyChanged = lastLocationKey.current !== location.key;
    
    // Update location key tracker
    if (locationKeyChanged) {
      lastLocationKey.current = location.key;
    }
    
    // Try to restore from location.state first, then fallback to sessionStorage
    let filtersToRestore = currentFilters;
    
    if (!filtersToRestore) {
      // Fallback to sessionStorage (for browser back button) - brand-specific
      try {
        const savedFiltersStr = sessionStorage.getItem(filterStorageKey);
        if (savedFiltersStr) {
          const parsedFilters = JSON.parse(savedFiltersStr);
          // Check if filters are older than 30 minutes (1800000 ms)
          const maxAge = 30 * 60 * 1000; // 30 minutes
          const now = Date.now();
          if (parsedFilters.timestamp && (now - parsedFilters.timestamp) > maxAge) {
            // Filters are too old, clear them
            sessionStorage.removeItem(filterStorageKey);
            filtersToRestore = null;
          } else {
            filtersToRestore = parsedFilters;
          }
        }
      } catch (e) {
        console.warn('Failed to restore filters from sessionStorage:', e);
      }
    }
    
    // Restore filters if we have them and location changed (navigated back)
    if (filtersToRestore && locationKeyChanged) {
      setCategoryCheck(filtersToRestore.categoryCheck || []);
      setBrandCheck(filtersToRestore.brandCheck || []);
      setSubBrandCheck(parseBrendiParam(searchParams));
      setSizeCheck(filtersToRestore.sizeCheck || []);
      setGtPrice(filtersToRestore.gtPrice);
      setLtPrice(filtersToRestore.ltPrice);
      setSortPrice(filtersToRestore.sortPrice || '');
      setOffers(filtersToRestore.offers !== undefined ? filtersToRestore.offers : "");
      setSelectedGender(filtersToRestore.selectedGender);
      hasRestoredFilters.current = true;
    }
  }, [location.state, location.key]);

  const filtersForNavigation = useMemo(() => {
    return {
      categoryCheck,
      brandCheck,
      subBrandCheck,
      sizeCheck,
      gtPrice,
      ltPrice,
      sortPrice,
      offers,
      selectedGender
    };
  }, [categoryCheck, brandCheck, subBrandCheck, sizeCheck, gtPrice, ltPrice, sortPrice, offers, selectedGender]);

  useEffect(() => {
    try {
      sessionStorage.setItem(filterStorageKey, JSON.stringify({ ...filtersForNavigation, timestamp: Date.now() }));
    } catch (e) {
      // ignore
    }
  }, [filterStorageKey, filtersForNavigation]);

  const handleLoadMore = () => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(urlPage + 1));
    setSearchParams(next, { replace: true });
    fetchNextPage();
  };

  const handleProductClick = () => {
    try {
      sessionStorage.setItem(plpScrollKey, String(window.scrollY || 0));
    } catch {}
  };

  const loadingText = isLoading ? 'Loading products....' : (products.length === 0 && !isFetching ? 'Ky brend është në proces rifreskimi. Qëndroni me ne – produktet e reja vijnë së shpejti!' : '');

  // Ensure we have loaded as many pages as the URL indicates (incremental fetch)
  useEffect(() => {
    if (navigationType === 'POP') return;

    if (!urlPage) return;
    if (isLoading || isFetching) return;
    if (!hasNextPage) return;
    if (loadedPagesCount >= urlPage) return;
    fetchNextPage();
  }, [urlPage, loadedPagesCount, hasNextPage, isLoading, isFetching, fetchNextPage, navigationType]);

  // Reset URL page to 1 when query args change (filters/gender/store)
  const lastArgsKeyRef = useRef('');
  const lastStoreCodeRef = useRef(storeCodeData);
  useEffect(() => {
    const argsKey = JSON.stringify(productsQueryParams);
    if (!lastArgsKeyRef.current) {
      lastArgsKeyRef.current = argsKey;
      lastStoreCodeRef.current = storeCodeData;
      return;
    }
    if (lastArgsKeyRef.current !== argsKey) {
      const prevStoreCode = lastStoreCodeRef.current;
      const nextStoreCode = storeCodeData;

      // When the page first mounts, storeCodeData is populated asynchronously.
      // That change should NOT reset page (otherwise Back always jumps to page=1).
      // Also, on browser Back/POP we should never force-reset page.
      const isInitialStoreCodeHydration = !prevStoreCode && !!nextStoreCode;
      const shouldSkipReset = navigationType === 'POP' || isInitialStoreCodeHydration;

      lastArgsKeyRef.current = argsKey;
      lastStoreCodeRef.current = nextStoreCode;

      if (shouldSkipReset) return;

      const next = new URLSearchParams(searchParams);
      next.set('page', '1');
      setSearchParams(next, { replace: true });
      try {
        sessionStorage.removeItem(plpScrollKey);
        sessionStorage.removeItem(plpProductKey);
      } catch {}
    }
  }, [productsQueryParams, storeCodeData, navigationType, searchParams, setSearchParams, plpScrollKey, plpProductKey]);

  // Scroll restoration (Back / POP / gesture) — state në sessionStorage është për brand-in aktual
  const didRestoreRef = useRef(false);
  useEffect(() => {
    didRestoreRef.current = false;
  }, [name]);

  useEffect(() => {
    // Vetëm kur kthehemi Back
    if (navigationType !== 'POP') return;
    if (didRestoreRef.current) return;
    
    const productId = (() => {
      try { return sessionStorage.getItem(plpProductKey); } catch { return null; }
    })();
    const scrollStr = (() => {
      try { return sessionStorage.getItem(plpScrollKey); } catch { return null; }
    })();

    // Nëse nuk ka asgjë për restore, mos prit
    if (!productId && !scrollStr) return;

    // Nëse nuk kemi ngarkuar ende faqet e nevojshme, prit
    if (loadedPagesCount < urlPage) return;

    // Blloko scroll-in automatik të browser-it
    if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    const initialY = (() => {
      if (!scrollStr) return null;
      const y = Number(scrollStr);
      return Number.isNaN(y) ? null : y;
    })();

    const alignToProduct = (attempt = 0) => {
      if (!productId) {
        didRestoreRef.current = true;
        try {
          sessionStorage.removeItem(plpScrollKey);
          sessionStorage.removeItem(plpProductKey);
        } catch {}
        return;
      }

      const el = document.getElementById(`product-${productId}`);
      if (el || attempt >= 10) {
        if (el) {
          const header =
            document.querySelector('header') ??
            document.querySelector('.header') ??
            document.querySelector('[class*="header"]');
          const headerRect = header ? header.getBoundingClientRect() : null;
          const headerHeight = headerRect ? headerRect.height : 0;
          const top = el.getBoundingClientRect().top + window.scrollY - headerHeight - 8;
          window.scrollTo({ top, behavior: 'instant' });
        }

        didRestoreRef.current = true;
        try {
          sessionStorage.removeItem(plpScrollKey);
          sessionStorage.removeItem(plpProductKey);
        } catch {}
        return;
      }

      window.setTimeout(() => alignToProduct(attempt + 1), 60);
    };

    const t = window.setTimeout(() => {
      // Hapi 1: shko afërsisht te pozicioni i ruajtur
      if (initialY !== null) {
        window.scrollTo(0, initialY);
      }
      // Hapi 2: pasi lista të jetë renderuar, ripozicionohu fiks te produkti
      alignToProduct(0);
    }, 50);

    return () => window.clearTimeout(t);
  }, [loadedPagesCount, urlPage, navigationType, plpScrollKey, plpProductKey]);

  const pagePromotionLabel = getPromotionLabelForLocation(
    promotionsByLocation,
    Array.isArray(storeCodeData) ? storeCodeData[0] : storeCodeData
  );

  // The sticky mobile Filtro button sits right under the fixed header, whose height varies.
  return (
    <div className="w-full">
      <Header />

      <div className="bg-white">
        <StoreHeaderBanner brandName={storeInfo?.brand} slug={name} />
        {/* Shirit promocioni: poshtë në vijë me WhatsApp */}
        {!promotionsLoading && pagePromotionLabel && (
          <div className="fixed bottom-5 left-3 right-3 md:left-24 md:right-24 z-[35] flex justify-center pointer-events-none">
            <div className="inline-block bg-black text-white text-center text-xs md:text-sm py-2.5 px-4 md:px-6 rounded shadow-lg max-w-full">
              {pagePromotionLabel}
            </div>
          </div>
        )}
        <div className="mx-auto px-4 pt-4 pb-0 md:py-5">
          <div className="flex flex-col items-center md:items-start gap-4 md:px-20">
            <div className="text-left md:text-left">
              <h2 className="text-2xl md:text-3xl font-semibold">{storeInfo?.brand}</h2>
            </div>
          </div>
        </div>

        {/* {filterData?.length > 0 && ( */}
        <div className="px-4">
          <div className="hidden lg:block">
            <Productfilter
              setCategoryPushData={setCategoryToggleByLength}
              setBrandPushData={setBrandPush}
              setSubBrandPushData={setSubBrandPush}
              setSizePushData={setSizePush}
              setPricePushData={setPricePush}
              setSortPushData={setSortPush}
              setOfferPush={setOfferPushData}
              setGenderPushData={setGenderPushData}
              filterBtn={() => { }}
              pageType="brand"
              availableSubBrands={availableSubBrands}
              resetFilter={resetFilter}
              activeOffer={offers}
              activeCategories={categoryCheck}
              activeBrands={brandCheck}
              activeSubBrands={subBrandCheck}
              activeSizes={sizeCheck}
              activeGender={selectedGender}
              activeSort={sortPrice}
              activeMinPrice={gtPrice}
              activeMaxPrice={ltPrice}
              productCount={productCount}
              facetStatus={facetStatus}
              availableSizes={brandFacets?.sizes}
              availableSizesByCategory={brandFacets?.sizesByCategory}
              availableCategoryIds={brandFacets?.categories}
              availableGenders={brandFacets?.genders}
            />

          </div>
        </div>
        {/* )} */}
      </div>

      {/* display: contents so the sticky Filtro button is constrained by the whole page, not this wrapper */}
      <div className="mobile-filter-bar brand-mobile-filter lg:hidden">
        <Productmobilefilter
          setCategoryPushData={setCategoryToggleByLength}
          setBrandPushData={setBrandPush}
          setSubBrandPushData={setSubBrandPush}
          setSizePushData={setSizePush}
          setPricePushData={setPricePush}
          setSortPushData={setSortPush}
          setOfferPush={setOfferPushData}
          setGenderPushData={setGenderPushData}
          filterBtn={() => { }}
          pageType="brand"
          sortInBar
          availableSubBrands={availableSubBrands}
          resetFilter={resetFilter}
          activeOffer={offers}
          activeCategories={categoryCheck}
          activeBrands={brandCheck}
          activeSubBrands={subBrandCheck}
          activeSizes={sizeCheck}
          activeGender={selectedGender}
          activeSort={sortPrice}
          activeMinPrice={gtPrice}
          activeMaxPrice={ltPrice}
          productCount={productCount}
          facetStatus={facetStatus}
          availableSizes={brandFacets?.sizes}
          availableSizesByCategory={brandFacets?.sizesByCategory}
          availableCategoryIds={brandFacets?.categories}
          availableGenders={brandFacets?.genders}
        />
      </div>

      <div className={`w-full flex items-center justify-center ${products?.length === 0 ? 'no-rec-sec' : ''}`}>
        {isLoading ? (
          <div className="flex justify-center items-center h-12 w-12 animate-spin rounded-full border-4 border-solid border-current border-r-transparent">
            <span className="sr-only">Loading...</span>
          </div>
        ) : isError ? (
          <div className="text-center">
            <div>Gabim gjatë ngarkimit të produkteve. Provoni përsëri.</div>
            <button className="mt-2 px-4 py-2 bg-black text-white" onClick={() => refetch()}>Rifresko</button>
          </div>
        ) : (
          products.length === 0 && !isFetching && <div>{loadingText}</div>
        )}
      </div>

      <div ref={listContainerRef} className="mx-auto container product-list">
        <InfiniteProducts
          products={products}
          addItemToWishlist={addItemToWishlist}
          onProductClick={handleProductClick}
          onLoadMore={handleLoadMore}
          hasNextPage={hasNextPage}
          isFetching={isFetching}
          filters={filtersForNavigation}
          plpStorageScope={plpStorageScope}
        />
      </div>

      {/* Load More button */}
      {hasNextPage && !isFetching && (
        <div className="flex justify-center my-6">
          <button
            className="px-6 py-2 bg-black text-white rounded disabled:opacity-50"
            onClick={handleLoadMore}
            disabled={isFetching}
          >
            Shfaq më shumë produkte
          </button>
        </div>
      )}

      {/* Loading spinner for next pages */}
      {isFetching && !isLoading && (
        <div className="flex justify-center my-4">
          <div className="h-8 w-8 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
        </div>
      )}

      <Footer />
    </div>
  );
}


